import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InboxOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import { cn } from '@/libs/utils';
import { downloadFile } from '@/services/files.service';

const MAX_FILE_SIZE_MB = 10;
const SUPPORTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'video/mp4',
  'video/webm',
  'video/ogg',
];

export default function UploadMultipleFile({
  className,
  onFilesChange,
  onFileRemove, // Callback for when a file should be deleted (receives file key/location)
  maxFileSize = MAX_FILE_SIZE_MB,
  supportedTypes = SUPPORTED_TYPES,
  maxFiles = 999,
  language, // Optional language override for localization
  initialFiles = [], // Array of {location, originalName, typeOfFile} objects
}) {
  const { t } = useTranslation(['messages']);
  const [hovering, setHovering] = useState(false);
  const [fileList, setFileList] = useState(
    initialFiles.map((file, index) => ({
      uid: `initial-${index}`,
      name: file.originalName || file.name || `File ${index + 1}`,
      status: 'done',
      url: file.location || file.url,
      type: file.typeOfFile || file.type,
    }))
  );

  // Update fileList when initialFiles changes (e.g., when data is loaded asynchronously)
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setFileList(
        initialFiles.map((file, index) => ({
          uid: `initial-${index}`,
          name: file.originalName || file.name || `File ${index + 1}`,
          status: 'done',
          url: file.location || file.url,
          type: file.typeOfFile || file.type,
        }))
      );
    }
  }, [initialFiles]);

  const handleChange = ({ fileList: newFileList }) => {
    // Check max files limit
    if (newFileList.length > maxFiles) {
      message.error(
        t('maxFilesExceeded', {
          ns: 'messages',
          max: maxFiles,
          lng: language,
        }) || `Maximum ${maxFiles} files allowed`
      );
      return;
    }

    // Validate only NEW files (those with originFileObj)
    // Keep existing files (those without originFileObj - already uploaded)
    const validFiles = newFileList.filter((file) => {
      // If file doesn't have originFileObj, it's already uploaded - keep it
      if (!file.originFileObj) {
        return true;
      }

      // Validate new files
      const isSupported = supportedTypes.includes(file.originFileObj.type);
      const isSizeOk = file.originFileObj.size / 1024 / 1024 <= maxFileSize;

      if (!isSupported || !isSizeOk) {
        message.error(
          `${file.originFileObj.name} - ${t('unsupportedFileType', { ns: 'messages', lng: language })}`
        );
        return false;
      }

      return true;
    });

    setFileList(validFiles);
    // Only send new files (with originFileObj) to parent
    onFilesChange(validFiles.map((f) => f.originFileObj).filter(Boolean));
  };

  const handleRemove = (file) => {
    // If file has URL (already uploaded to S3), notify parent to mark for deletion
    if (file.url && onFileRemove) {
      // Extract S3 key from URL
      const [, key] = file.url.split('.com/'); // e.g., "questionnaire-docs/companyId/filename.jpg"
      onFileRemove(key, file);
    }

    // Remove from local fileList
    const updated = fileList.filter((f) => f.uid !== file.uid);
    setFileList(updated);

    // Update parent with new files (exclude files with URLs - those are already uploaded)
    onFilesChange(updated.map((f) => f.originFileObj).filter(Boolean));
  };

  const handlePreview = async (file) => {
    // For files that have URL (uploaded to S3), extract key and get presigned URL
    if (file.url) {
      try {
        // Extract S3 key from full URL
        const url = new URL(file.url);
        const key = url.pathname.substring(1); // Remove leading '/'

        // Get presigned URL from backend
        const response = await downloadFile({ key });

        // Open in new tab - response is { url: "..." }
        window.open(response.url, '_blank');
      } catch (error) {
        console.error('Failed to preview file:', error);
        message.error(
          t('failedToPreviewFile', { ns: 'messages', lng: language }) ||
            'Failed to preview file'
        );
      }
    }
  };

  return (
    <Upload.Dragger
      multiple
      beforeUpload={() => false}
      fileList={fileList}
      onChange={handleChange}
      onRemove={handleRemove}
      onPreview={handlePreview}
      accept={supportedTypes.join(',')}
      className={cn(
        'rounded-lg p-6 pt-10 transition-all duration-300',
        className
      )}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="flex min-h-[150px] flex-col items-center justify-center">
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>

        <p className="ant-upload-text">
          {hovering
            ? t('dropFilesHereToUpload', { ns: 'messages', lng: language })
            : t('clickDragFileAareUpload', { ns: 'messages', lng: language })}
        </p>

        <p className="ant-upload-hint text-center text-xs leading-5 text-gray-500">
          {hovering ? (
            <>
              {t('supportedTypes', { ns: 'messages', lng: language })}: JPG,
              PNG, GIF, WEBP, PDF, DOC, DOCX, XLS, XLSX, MP4, WEBM, OGG.
              <br />
              {t('maxFileSize', { ns: 'messages', lng: language })}:{' '}
              {MAX_FILE_SIZE_MB}MB.
            </>
          ) : (
            t('supportForSingleMultipleFileUploads', {
              ns: 'messages',
              lng: language,
            })
          )}
        </p>
      </div>
    </Upload.Dragger>
  );
}
